import { memo, useMemo, CSSProperties, FC } from 'react';
// Import Helpers
import { getSafeHttpUrl } from 'services/Helpers';
// Import Enums
import { DeviceTypeEnum, DisplayModeEnum } from 'enums';

type DisplayMode = DisplayModeEnum;
type DisplaySize = DeviceTypeEnum;

interface Button {
    Text: string;
    Url: string;
}

interface TelegramPreviewProps {
    text?: string;
    mediaUrl?: string;
    buttons?: Button[][];
    displayMode?: DisplayMode;
    displaySize?: DisplaySize;
}

const TelegramPreview: FC<TelegramPreviewProps> = ({
    text,
    mediaUrl,
    buttons,
    displayMode = DisplayModeEnum.LightMode,
    displaySize = DeviceTypeEnum.Desktop,
}) => {
    const styles = useMemo(() => {
        const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
        const sizes: Record<DisplaySize, { width: string; fontSize: string }> = {
            [DeviceTypeEnum.Mobile]: { width: '320px', fontSize: '15px' },
            [DeviceTypeEnum.Tablet]: { width: '450px', fontSize: '16px' },
            [DeviceTypeEnum.Desktop]: { width: '600px', fontSize: '17px' },
            [DeviceTypeEnum.FullScreen]: { width: '1024px', fontSize: '17px' },
        };
        const palettes: Record<
            DisplayMode,
            { bg: string; text: string; time: string; buttonBg: string; buttonText: string }
        > = {
            [DisplayModeEnum.DarkMode]: {
                bg: 'linear-gradient(to right, #2a3642, #374758)',
                text: '#ffffff',
                time: '#708499',
                buttonBg: 'linear-gradient(to right, #2a3642, #374758)',
                buttonText: '#ffffff',
            },
            [DisplayModeEnum.LightMode]: {
                bg: 'linear-gradient(to right, #dadae0, #e5e5ea)',
                text: '#000000',
                time: '#a1a1a6',
                buttonBg: 'linear-gradient(to right, #dadae0, #e5e5ea)',
                buttonText: '#000000',
            },
        };
        const currentPalette = palettes[displayMode];
        const currentSize = sizes[displaySize];
        const buttonStyle: CSSProperties = {
            flex: 1,
            padding: '8px',
            background: currentPalette.buttonBg,
            color: currentPalette.buttonText,
            textAlign: 'center',
            borderRadius: '8px',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 500,
            fontFamily,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        };

        return {
            mainContainer: {
                width: currentSize.width,
            } as CSSProperties,
            mediaAndTextContainer: {
                width: '100%',
                background: currentPalette.bg,
                borderRadius: '12px',
                overflow: 'hidden',
                fontFamily,
                display: 'flex',
                flexDirection: 'column',
            } as CSSProperties,
            mediaContainer: {
                position: 'relative',
            } as CSSProperties,
            media: {
                width: '100%',
                maxHeight: '400px',
                objectFit: 'cover',
                display: 'block',
            } as CSSProperties,
            time: {
                position: 'absolute',
                bottom: '8px',
                right: '14px',
                fontSize: '12px',
                color: currentPalette.time,
            } as CSSProperties,
            mediaTime: {
                position: 'absolute',
                bottom: '8px',
                fontSize: '12px',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                color: '#ffffff',
                padding: '2px 8px',
                borderRadius: '12px',
                right: '8px',
            } as CSSProperties,
            textContainer: {
                padding: '10px 14px 25px',
                color: currentPalette.text,
                fontSize: currentSize.fontSize,
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                position: 'relative',
            } as CSSProperties,
            buttonsContainer: {
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '3px',
                marginTop: '3px',
            } as CSSProperties,
            buttonsRow: {
                display: 'flex',
                columnGap: '3px',
            } as CSSProperties,
            button: buttonStyle,
        };
    }, [displayMode, displaySize]);

    return (
        <div style={styles.mainContainer}>
            <div style={styles.mediaAndTextContainer}>
                {mediaUrl && (
                    <div style={styles.mediaContainer}>
                        <img src={mediaUrl} alt="Telegram media preview" style={styles.media} />
                        {!text && <div style={styles.mediaTime}>00:00</div>}
                    </div>
                )}
                {text && (
                    <div style={{ ...styles.textContainer, paddingTop: mediaUrl ? '10px' : '14px' }}>
                        {text}
                        <div style={styles.time}>00:00</div>
                    </div>
                )}
            </div>
            {buttons && buttons.length > 0 && (
                <div style={styles.buttonsContainer}>
                    {buttons.map((row, rowIndex) => (
                        <div key={rowIndex} style={styles.buttonsRow}>
                            {row.map((button, buttonIndex) => (
                                <a
                                    key={buttonIndex}
                                    href={getSafeHttpUrl(button.Url)}
                                    title={button.Url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.button}
                                >
                                    {button.Text}
                                </a>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(TelegramPreview);
